import {FC, Fragment, useCallback, useEffect, useState} from 'react';
import {Transition} from "@headlessui/react";
import {AppDispatch} from "../../../store";
import {useDispatch, useSelector} from "react-redux";
import {closeApprovalModel, payoutAddressesSelector, setPayoutAddress} from "../../../store/payout-addresses.store.ts";
import {useMutation} from "@apollo/client";
import {ApprovePayoutAddressResponseType} from "../../types/payment/approve-payout-address-response.type.ts";
import {APPROVE_PAYOUT_ADDRESS_MUTATION} from "../../../api/graphql/mutations/approve-payout-address.mutation.ts";
import NotificationToast from "../toasts/notification.toast.tsx";
import {NotificationType} from "../../enums/notification.enum.ts";
import {toast} from "react-toastify";
import {userSelector} from "../../../store/user.store.ts";


const ApprovePayoutAddressModal: FC = () => {
    const dispatcher: AppDispatch = useDispatch();
    const [show, setShow] = useState<boolean>(false);
    const [name, setName] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [tfaCode, setTfaCode] = useState<string>();
    const [showTfaField, setShowTfaField] = useState<boolean>(false);

    const {profile: {isTfaEnabled}} = useSelector(userSelector);
    const {approvalModel: {addressId}, payoutAddress} = useSelector(payoutAddressesSelector);

    const [approveAddress, approveAddressData] = useMutation<ApprovePayoutAddressResponseType>(APPROVE_PAYOUT_ADDRESS_MUTATION);

    useEffect(() => {
        setShowTfaField(isTfaEnabled);
    }, [isTfaEnabled]);

    useEffect((): void => {
        if (!addressId) {
            setShow(false)
        } else {
            setShow(true)
            setName(payoutAddress?.name || '')
            setDescription(payoutAddress?.description || '')
        }

    }, [payoutAddress?.description, payoutAddress?.name, addressId]);

    useEffect((): void => {
        if (!show) {
            dispatcher(closeApprovalModel(null));
            dispatcher(setPayoutAddress(null));
            setName('');
            setDescription('');
            setTfaCode('');
        }
    }, [dispatcher, show]);

    useEffect(() => {
        if (approveAddressData.error) {
            toast(<NotificationToast type={NotificationType.WARNING} title="Payout Address" message={approveAddressData.error.message} />, {
                toastId: 'approve-address-error'
            });
        }
    }, [approveAddressData.error]);

    useEffect(() => {
        if (approveAddressData.data) {
            toast(<NotificationToast type={NotificationType.SUCCESS} title="Payout Address" message="Successfully approved" />, {
                toastId: 'approve-address-success'
            });

            setShow(false);
        }
    }, [approveAddressData.data]);

    const approve = useCallback(async () => {
        await approveAddress({
            variables: {
                dto: {
                    addressId: addressId,
                    name,
                    description
                },
                tfaCode
            }
        });
    }, [approveAddress, addressId, name, description, tfaCode]);

    return (
        <>
            <Transition
                as={Fragment}
                show={show}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-80"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
            >
                <div className="fixed inset-0 bg-slate-900 bg-opacity-30 z-50 transition-opacity"></div>
            </Transition>
            <Transition
                as={Fragment}
                show={show}
                enter="transition ease-in-out duration-200"
                enterFrom="opacity-0 translate-y-4"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in-out duration-200"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-4"
            >
                <div
                    className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center transform px-4 sm:px-6">
                    <div onClick={(e) => e.stopPropagation()}
                         className="bg-white rounded shadow-lg overflow-auto max-w-lg w-full max-h-full">

                        {/* Modal header */}
                        <div className="p-4">
                            <div className="text-lg font-semibold text-slate-800">Approve Payout Address</div>
                        </div>
                        <div className='divide-y'>
                            <div></div>
                            <div></div>
                        </div>
                        {/* Modal content */}
                        <div className="p-4 pb-0">
                            <div className="text-xs italic mb-6 text-center">
                                By submitting this form, you confirm that this is a trusted address and all further
                                payments will be processed automatically.
                            </div>

                            <div className="text-sm font-bold text-center mb-4">{payoutAddress?.address}</div>

                            <label className="block text-sm font-medium mb-1" htmlFor="safe_password">Owner Name:</label>
                            <div className="relative mb-2">
                                <input
                                    id='name'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Name"
                                    className='form-input w-full max-w-[646px] mr-1'
                                    type="text"
                                />
                            </div>

                            <label className="block text-sm font-medium mb-1" htmlFor="safe_password">Description:</label>
                            <div className="relative">
                                <input
                                    id='name'
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Description"
                                    className='form-input w-full max-w-[646px] mr-1'
                                    type="text"
                                />
                            </div>

                            <Transition
                                as={Fragment}
                                show={showTfaField}
                                enter="transition ease-in-out duration-600"
                                enterFrom="opacity-0 translate-y-4"
                                enterTo="opacity-100 translate-y-0"
                                leave="transition ease-in-out duration-300"
                                leaveFrom="opacity-100 translate-y-0"
                                leaveTo="opacity-0 translate-y-4"
                            >
                                <div className="mt-2">
                                    <label className="block text-sm font-medium mb-1" htmlFor="tfa_code">TFA Code:</label>
                                    <div className="relative">
                                        <input
                                            name="tfa_code"
                                            value={tfaCode}
                                            onChange={(e) => setTfaCode(e.target.value)}
                                            placeholder="TFA Code"
                                            className='form-input w-full max-w-[646px] mr-1'
                                            type="text"
                                        />
                                    </div>
                                </div>
                            </Transition>
                        </div>
                        {/* Modal footer */}

                        <div className="flex justify-end p-4">
                            <button onClick={() => setShow(false)}
                                    className='bg-white border text-gray-700 hover:bg-gray-100 hover btn-sm whitespace-nowrap mr-2'>
                                Cancel
                            </button>
                            <button
                                onClick={approve}
                                className={`${approveAddressData.loading && 'opacity-40'} bg-indigo-500 hover:bg-indigo-600 btn-sm text-white whitespace-nowrap`}>
                                {approveAddressData.loading
                                    ? <svg className="animate-spin w-4 h-4 fill-current shrink-0 mr-2" viewBox="0 0 16 16">
                                        <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                                    </svg>
                                    : ''
                                }
                                Approve
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>
        </>
    );
};

export default ApprovePayoutAddressModal;
